import type * as Geo from "@/base/geo"
import type { EntityState, PayloadAction, Reducer } from "@reduxjs/toolkit"

import { loadActiveFarmAsync } from "@/base/load-app"
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit"

import { signOutAsync } from "./auth.reducer"
import type { RootState } from "@/redux/root.reducer"

/**
 * A named point of interest on a linear-move irrigator. The
 * `location` is the source of truth; `distanceFromStartMeters` is
 * annotated by the backend (computed via PostGIS
 * ``ST_LineLocatePoint`` against the parent's path) so the frontend
 * doesn't need to recompute it.
 */
export interface LinearIrrigatorSlot {
  id: number
  name: string
  location: Geo.PointGeoJson
  /** Projected distance from the parent's path start, in meters.
   * Null when the parent has no path configured. */
  distanceFromStartMeters: number | null
}

export interface LinearIrrigator {
  id: number
  name: string
  make: string | null
  model: string | null
  farmId: number
  /** 2-point LineString representing the path the span travels along.
   * Drawn as a rectangle on the map together with `spanWidthMeters`. */
  pathGeometry: Geo.LineStringGeoJson | null
  /** Span heading (perpendicular to motion), degrees clockwise from
   * north. Used for visualization. */
  spanHeadingDegrees: number | null
  /** Span width in meters; the rectangle drawn on the map is
   * `pathGeometry length × spanWidthMeters`. */
  spanWidthMeters: number | null
  /** Slots ordered by distance from path start. */
  slots: LinearIrrigatorSlot[]
  /** Average flow rate in liters per minute (for speed-by-depth calculation). */
  averageFlowRateLpm: number | null
  /** Linear ground speed at the controller's maximum (100%) setting, m/h. */
  maxSpeedMetersPerHour: number | null
  /** Linear ground speed at the slowest non-zero controller setting, m/h. */
  minSpeedMetersPerHour: number | null
  /** Calibrated starting controller percent for linears without
   * speed feedback. */
  initialSpeedPercent: number | null
  controllerDeviceId: string | null
  monitorDeviceId: string | null
  isActive: boolean
  createDate?: string
}

/**
 * Shape of a LinearIrrigator as it arrives from the API (after
 * camelcaseKeys). The backend still ships the legacy
 * boundary_start_distance_meters / boundary_end_distance_meters
 * fields for compatibility, but the frontend ignores them now —
 * the path's start/end already defines the run extent.
 */
export interface LinearIrrigatorPayload extends LinearIrrigator {
  boundaryStartDistanceMeters?: number | null
  boundaryEndDistanceMeters?: number | null
}

export function prepareLinearIrrigator(
  payload: LinearIrrigatorPayload,
): LinearIrrigator {
  const {
    boundaryStartDistanceMeters: _ignoredStart,
    boundaryEndDistanceMeters: _ignoredEnd,
    ...rest
  } = payload
  return rest
}

const adapter = createEntityAdapter<LinearIrrigator, number>({
  selectId: (linear) => linear.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
})

const initialState = adapter.getInitialState()

const linearIrrigatorsSlice = createSlice({
  name: "linearIrrigators",
  initialState,
  reducers: {
    upsertLinearIrrigator: (
      state,
      action: PayloadAction<LinearIrrigatorPayload>,
    ) => {
      adapter.upsertOne(state, prepareLinearIrrigator(action.payload))
    },
    upsertManyLinearIrrigators: (
      state,
      action: PayloadAction<LinearIrrigatorPayload[]>,
    ) => {
      adapter.upsertMany(
        state,
        action.payload.map(prepareLinearIrrigator),
      )
    },
    removeLinearIrrigator: adapter.removeOne,
  },
  extraReducers: (builder) =>
    builder
      .addCase(signOutAsync.fulfilled, () => initialState)
      .addCase(loadActiveFarmAsync.fulfilled, (state, { payload }) => {
        if (payload.linearIrrigators) {
          adapter.setAll(
            state,
            payload.linearIrrigators.map(prepareLinearIrrigator),
          )
        }
      }),
})

export const {
  upsertLinearIrrigator,
  upsertManyLinearIrrigators,
  removeLinearIrrigator,
} = linearIrrigatorsSlice.actions

const selectors = adapter.getSelectors<RootState>(
  (state) => state.linearIrrigators,
)

export const {
  selectAll: selectAllLinearIrrigators,
  selectById: selectLinearIrrigatorById,
  selectIds: selectLinearIrrigatorIds,
  selectEntities: selectLinearIrrigatorEntities,
  selectTotal: selectLinearIrrigatorTotal,
} = selectors

/**
 * Get the LinearIrrigator associated with a device configuration, if any.
 */
export function selectLinearIrrigatorForDevice(
  state: RootState,
  linearIrrigatorId: number | null | undefined,
): LinearIrrigator | undefined {
  if (!linearIrrigatorId) return undefined
  return selectLinearIrrigatorById(state, linearIrrigatorId)
}

export const linearIrrigatorsReducer: Reducer<
  EntityState<LinearIrrigator, number>
> = linearIrrigatorsSlice.reducer
